import { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Menu, Plus, FileDown, Pencil, Trash2, Share2, Search,
  FileText, Calculator, Clock, Send, Inbox, CheckCircle,
} from "lucide-react";

export default function QuotesPage() {
  const { authAxios, user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const basePath = isAdmin ? "/admin" : "/agent";
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [sendConfirm, setSendConfirm] = useState(null);
  const [shareUrl, setShareUrl] = useState(null);

  const fetchQuotes = useCallback(async () => {
    try {
      const res = await authAxios.get("/quotes");
      setQuotes(res.data);
    } catch { toast.error("Errore caricamento preventivi"); }
    finally { setLoading(false); }
  }, [authAxios]);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  // Derived lists
  const myQuotes = useMemo(() => quotes.filter(q => q.created_by === user?.id), [quotes, user]);
  const myDrafts = useMemo(() => myQuotes.filter(q => q.status === "draft"), [myQuotes]);
  const mySent = useMemo(() => myQuotes.filter(q => q.status === "sent"), [myQuotes]);
  const receivedQuotes = useMemo(() => quotes.filter(q => q.created_by !== user?.id && q.status === "sent"), [quotes, user]);

  const filterQuotes = (list) => {
    if (!searchQuery) return list;
    const s = searchQuery.toLowerCase();
    return list.filter(q =>
      q.numero.toLowerCase().includes(s) ||
      q.client_name.toLowerCase().includes(s) ||
      (q.created_by_name || "").toLowerCase().includes(s)
    );
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await authAxios.delete(`/quotes/${deleteConfirm.id}`);
      toast.success("Preventivo eliminato");
      setDeleteConfirm(null);
      fetchQuotes();
    } catch { toast.error("Errore eliminazione"); }
  };

  const handleSend = async () => {
    if (!sendConfirm) return;
    try {
      await authAxios.post(`/quotes/${sendConfirm.id}/send`);
      toast.success("Preventivo inviato all'admin");
      setSendConfirm(null);
      fetchQuotes();
    } catch { toast.error("Errore invio"); }
  };

  const downloadPdf = async (quoteId) => {
    try {
      const res = await authAxios.post(`/quotes/${quoteId}/pdf`, {}, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "preventivo.pdf"; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Errore PDF"); }
  };

  const handleShare = async (quoteId) => {
    try {
      const res = await authAxios.post(`/quotes/${quoteId}/share`);
      const fullUrl = `${window.location.origin}${res.data.share_url}`;
      setShareUrl(fullUrl);
      toast.success("Link generato");
    } catch { toast.error("Errore condivisione"); }
  };

  const QuoteTable = ({ list, showAgent = false, showSendBtn = false }) => {
    const filtered = filterQuotes(list);
    if (loading) return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500" />
      </div>
    );
    if (filtered.length === 0) return (
      <div className="py-12 text-center text-zinc-500">
        <FileText className="w-10 h-10 mx-auto mb-2 text-zinc-700" />
        <p>Nessun preventivo</p>
      </div>
    );
    return (
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-white/5 hover:bg-transparent">
              <TableHead className="text-zinc-400">Numero</TableHead>
              <TableHead className="text-zinc-400">Cliente</TableHead>
              {showAgent && <TableHead className="text-zinc-400 hidden md:table-cell">Agente</TableHead>}
              <TableHead className="text-zinc-400 hidden sm:table-cell">Articoli</TableHead>
              <TableHead className="text-zinc-400 text-right">Totale</TableHead>
              <TableHead className="text-zinc-400 hidden sm:table-cell">Data</TableHead>
              <TableHead className="text-zinc-400 text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(q => (
              <TableRow key={q.id} className="border-white/5">
                <TableCell className="font-mono text-amber-400 font-medium">{q.numero}</TableCell>
                <TableCell>
                  <p className="text-white text-sm font-medium">{q.client_name}</p>
                  {q.client_citta && <p className="text-xs text-zinc-500">{q.client_citta}</p>}
                </TableCell>
                {showAgent && (
                  <TableCell className="text-zinc-400 text-sm hidden md:table-cell">
                    {q.created_by_name || "-"}
                    {q.cod_agente != null && <span className="text-zinc-600 ml-1">(Ag. {q.cod_agente})</span>}
                  </TableCell>
                )}
                <TableCell className="text-zinc-400 font-mono hidden sm:table-cell">{q.items?.length || 0}</TableCell>
                <TableCell className="text-right font-mono text-white font-medium">{q.total?.toFixed(2)} EUR</TableCell>
                <TableCell className="text-zinc-400 text-sm hidden sm:table-cell">
                  {q.created_at ? new Date(q.created_at).toLocaleDateString("it-IT") : "-"}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {q.status === "draft" && (
                      <Button size="sm" variant="ghost" onClick={() => navigate(`${basePath}/quotes/${q.id}/edit`)}
                        className="text-zinc-400 hover:text-amber-400" title="Modifica">
                        <Pencil className="w-4 h-4" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => downloadPdf(q.id)}
                      className="text-zinc-400 hover:text-emerald-400" title="Scarica PDF">
                      <FileDown className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleShare(q.id)}
                      className="text-zinc-400 hover:text-blue-400" title="Condividi">
                      <Share2 className="w-4 h-4" />
                    </Button>
                    {showSendBtn && q.status === "draft" && (
                      <Button size="sm" variant="ghost" onClick={() => setSendConfirm(q)}
                        className="text-zinc-400 hover:text-cyan-400" title="Invia all'Admin"
                        data-testid={`send-quote-${q.id}`}>
                        <Send className="w-4 h-4" />
                      </Button>
                    )}
                    {q.status === "draft" && (
                      <Button size="sm" variant="ghost" onClick={() => setDeleteConfirm(q)}
                        className="text-zinc-400 hover:text-red-400" title="Elimina">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="quotes-page">
      <main className="p-4 sm:p-6 lg:p-8">
        <PageHeader title="Preventivi" />
        <div className="mb-6 flex items-center gap-4">
          <div className="flex-1">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-1">Preventivi</h1>
            <p className="text-zinc-400 text-sm">Crea, gestisci e invia preventivi</p>
          </div>
          <Button onClick={() => navigate(`${basePath}/quotes/new`)}
            className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="new-quote-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuovo Preventivo
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {isAdmin ? (
            <>
              <StatCard label="I Miei" value={myQuotes.length} icon={FileText} color="text-blue-400" bg="bg-blue-500/10" />
              <StatCard label="Bozze" value={myDrafts.length} icon={Clock} color="text-amber-400" bg="bg-amber-500/10" />
              <StatCard label="Ricevuti" value={receivedQuotes.length} icon={Inbox} color="text-cyan-400" bg="bg-cyan-500/10" />
              <StatCard label="Valore Totale" value={`${quotes.reduce((s, q) => s + (q.total || 0), 0).toFixed(0)}`}
                icon={Calculator} color="text-purple-400" bg="bg-purple-500/10" suffix=" EUR" />
            </>
          ) : (
            <>
              <StatCard label="Totale" value={myQuotes.length} icon={FileText} color="text-blue-400" bg="bg-blue-500/10" />
              <StatCard label="Bozze" value={myDrafts.length} icon={Clock} color="text-amber-400" bg="bg-amber-500/10" />
              <StatCard label="Inviati" value={mySent.length} icon={Send} color="text-cyan-400" bg="bg-cyan-500/10" />
              <StatCard label="Valore Totale" value={`${myQuotes.reduce((s, q) => s + (q.total || 0), 0).toFixed(0)}`}
                icon={Calculator} color="text-purple-400" bg="bg-purple-500/10" suffix=" EUR" />
            </>
          )}
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input placeholder="Cerca per numero, cliente, agente..."
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#18181b] border-white/10" data-testid="quote-search" />
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue={isAdmin ? "mine" : "drafts"} className="space-y-4">
          <TabsList className="bg-[#18181b] border border-white/5">
            {isAdmin ? (
              <>
                <TabsTrigger value="mine" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black" data-testid="tab-mine">
                  <FileText className="w-4 h-4 mr-2" /> I Miei ({myQuotes.length})
                </TabsTrigger>
                <TabsTrigger value="received" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-black" data-testid="tab-received">
                  <Inbox className="w-4 h-4 mr-2" /> Ricevuti ({receivedQuotes.length})
                </TabsTrigger>
              </>
            ) : (
              <>
                <TabsTrigger value="drafts" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black" data-testid="tab-drafts">
                  <Clock className="w-4 h-4 mr-2" /> Bozze ({myDrafts.length})
                </TabsTrigger>
                <TabsTrigger value="sent" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-black" data-testid="tab-sent">
                  <Send className="w-4 h-4 mr-2" /> Inviati ({mySent.length})
                </TabsTrigger>
              </>
            )}
          </TabsList>

          {isAdmin ? (
            <>
              <TabsContent value="mine">
                <Card className="bg-[#18181b] border-white/5">
                  <CardContent className="p-0">
                    <QuoteTable list={myQuotes} />
                  </CardContent>
                </Card>
              </TabsContent>
              <TabsContent value="received">
                <Card className="bg-[#18181b] border-white/5">
                  <CardContent className="p-0">
                    <QuoteTable list={receivedQuotes} showAgent />
                  </CardContent>
                </Card>
              </TabsContent>
            </>
          ) : (
            <>
              <TabsContent value="drafts">
                <Card className="bg-[#18181b] border-white/5">
                  <CardContent className="p-0">
                    <QuoteTable list={myDrafts} showSendBtn />
                  </CardContent>
                </Card>
              </TabsContent>
              <TabsContent value="sent">
                <Card className="bg-[#18181b] border-white/5">
                  <CardContent className="p-0">
                    <QuoteTable list={mySent} />
                  </CardContent>
                </Card>
              </TabsContent>
            </>
          )}
        </Tabs>

        {/* Send to Admin Dialog */}
        <Dialog open={!!sendConfirm} onOpenChange={() => setSendConfirm(null)}>
          <DialogContent className="bg-[#18181b] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Send className="w-5 h-5 text-cyan-400" /> Invia all'Admin
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                Inviare il preventivo <strong className="text-amber-400">{sendConfirm?.numero}</strong> per <strong className="text-white">{sendConfirm?.client_name}</strong> all'admin?
                <br /><span className="text-zinc-500 text-xs mt-1 block">Dopo l'invio non sara piu modificabile.</span>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSendConfirm(null)} className="border-white/10">Annulla</Button>
              <Button onClick={handleSend} className="bg-cyan-500 hover:bg-cyan-600 text-black" data-testid="confirm-send-btn">
                <Send className="w-4 h-4 mr-2" /> Invia
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="bg-[#18181b] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Elimina Preventivo</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Eliminare il preventivo <strong className="text-amber-400">{deleteConfirm?.numero}</strong>?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="border-white/10">Annulla</Button>
              <Button onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white">Elimina</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Share URL Dialog */}
        <Dialog open={!!shareUrl} onOpenChange={() => setShareUrl(null)}>
          <DialogContent className="bg-[#18181b] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Link Condivisione</DialogTitle>
            </DialogHeader>
            <div className="flex gap-2">
              <Input value={shareUrl || ""} readOnly className="bg-[#27272a] border-white/10 text-xs font-mono" />
              <Button onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Copiato!"); }}
                className="bg-amber-500 hover:bg-amber-600 text-black shrink-0">Copia</Button>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShareUrl(null)} className="border-white/10">Chiudi</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, bg, suffix = "" }) {
  return (
    <Card className="bg-[#18181b] border-white/5">
      <CardContent className="p-3 flex items-center gap-3">
        <div className={`p-2 rounded-lg ${bg}`}>
          <Icon className={`w-4 h-4 ${color}`} />
        </div>
        <div>
          <p className={`text-xl font-bold font-mono ${color}`}>{value}{suffix}</p>
          <p className="text-[11px] text-zinc-500">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}
